"use client";

import { useState } from "react";
import Image from "next/image";
import { ExternalLink, Smartphone } from "lucide-react";

export default function PortfolioSection() {
  const [activeFilter, setActiveFilter] = useState("all");

  const projects = [
    {
      id: 1,
      title: "Ostva - Rental App",
      category: "mobile",
      description:
        "A sharing-economy marketplace where users rent items or offer tasks locally – earn or save in one handy app.",
      image: "/images/portfolio/ostva.jpg",
      technologies: ["React Native", "Node.js", "AWS", "Stripe", "KYC"],
      liveUrl: "https://www.ostva.com/",
    },
    {
      id: 2,
      title: "Trinity Guard - Patrol Tracking App",
      category: "mobile",
      description:
        "Cloud-based, AI-powered patrol & duty-roster app for security companies to track guards, incidents & shift compliance.",
      image: "/images/portfolio/trinityguard.jpg",
      technologies: [
        "React Native",
        "Node.js",
        "Firebase",
        "Stripe",
        "AI",
        "AWS",
      ],
      liveUrl: "https://trinity-guard.com/en",
    },
    {
      id: 3,
      title: "MyCalamari - Tourist Guide App",
      category: "mobile",
      description:
        "Interactive self-guided sightseeing game app that leads you through adventure trails, photo-points & quizzes in fun style.",
      image: "/images/portfolio/mycalamari.jpg",
      technologies: ["React Native", "Node.js", "IAP", "Navigation"],
      liveUrl: "https://mycalamari.com/en",
    },
    {
      id: 4,
      title: "GetFlyMate - Hour Building App",
      category: "mobile",
      description:
        "Interactive self-guided sightseeing game app that leads you through adventure trails, photo-points & quizzes in fun style.",
      image: "/images/portfolio/getflymate.jpg",
      technologies: ["React Native", "Push Notifications", "Node.JS", "MySQL"],
      liveUrl: "https://getflymate.com/en",
    },
    {
      id: 5,
      title: "Axereal - B2B Introduction App",
      category: "mobile",
      description:
        "Official mobile app for the Axereal agricultural group, updated news and product catalogs for growers and partners.",
      image: "/images/portfolio/axereal.jpg",
      technologies: ["React Native", "Cataloges", "Node.JS", "MySQL"],
      liveUrl: "https://apps.apple.com/us/app/axereal/id6449256574",
    },
    {
      id: 6,
      title: "ServiceBoard - Service Management App",
      category: "mobile",
      description:
        "ServiceBoard is a service management app for organizing field tasks, tracking service requests, managing clients, and coordinating technicians.",
      image: "/images/portfolio/serviceboard.jpg",
      technologies: ["React Native", "Firebase", "AWS", "Push Notifications"],
      liveUrl: "#",
    },
    {
      id: 7,
      title: "Driveral",
      category: "web",
      description:
        "Europe-wide driver job portal available in 21 languages; job-seekers and transport firms matched on one platform.",
      image: "/images/portfolio/driveral.jpg",
      technologies: ["HTML", "CSS", "JavaScript", "PHP", "MySQL", "Barion"],
      liveUrl: "https://driveral.com/en/",
    },
    {
      id: 8,
      title: "Open Horizon Aviation",
      category: "web",
      description:
        "Global aviation training & recruitment provider offering charter services, pilot upgrades and crew sourcing.",
      image: "/images/portfolio/openhorizon.jpg",
      technologies: ["React.JS", "Node.js", "MySQL"],
      liveUrl: "https://open-horizon-aviation.com/",
    },
    {
      id: 9,
      title: "AI Sport Tips",
      category: "web",
      description:
        "Sports-betting content platform using AI-algorithms to deliver high-odds tips and analytics for informed players.",
      image: "/images/portfolio/aitips.jpg",
      technologies: ["React", "Node.js", "Next.JS", "Stripe"],
      liveUrl: "https://ai-sport-tips.com/en",
    },
    {
      id: 10,
      title: "Driver-Jobs UK ",
      category: "web",
      description:
        "Job-search site dedicated to professional drivers in the UK and Europe, offering categorized vacancies in transport and logistics sectors.",
      image: "/images/portfolio/driverjobs.jpg",
      technologies: ["React", "Node.JS", "Custom API", "Payment Gateway"],
      liveUrl: "https://driver-jobs.uk/",
    },
    {
      id: 11,
      title: "Mercor GUIDE",
      category: "web",
      description:
        "A fire-protection specification app and tool, helping engineers choose fire-retardant solutions for building projects.",
      image: "/images/portfolio/mercor.jpg",
      technologies: ["React", "Node.JS", "Multi language engine", "Regions"],
      liveUrl: "https://guide.dunamenti.hu/en",
    },
  ];

  const filters = [
    { key: "all", label: "All Projects" },
    { key: "mobile", label: "Mobile Apps" },
    { key: "web", label: "Web Apps" },
  ];

  const filteredProjects =
    activeFilter === "all"
      ? projects
      : projects.filter((project) => project.category === activeFilter);

  return (
    <section id="portfolio" className="py-20 bg-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="text-center mb-16">
          <h2 className="text-3xl sm:text-4xl font-bold text-secondary-900 mb-6">
            Our Portfolio
          </h2>
          <p className="text-xl text-secondary-600 max-w-3xl mx-auto">
            Explore our recent projects and see how we've helped businesses
            transform their ideas into successful mobile applications.
          </p>
        </div>

        {/* Filter Buttons */}
        <div className="flex flex-wrap justify-center gap-4 mb-12">
          {filters.map((filter) => (
            <button
              key={filter.key}
              onClick={() => setActiveFilter(filter.key)}
              className={`px-6 py-3 rounded-lg font-medium transition-all duration-200 ${
                activeFilter === filter.key
                  ? "bg-primary-600 text-white shadow-lg"
                  : "bg-secondary-100 text-secondary-700 hover:bg-secondary-200"
              }`}
            >
              {filter.label}
            </button>
          ))}
        </div>

        {/* Projects Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8">
          {filteredProjects.map((project, index) => (
            <div
              key={project.id}
              className="bg-white rounded-xl overflow-hidden shadow-lg hover:shadow-xl transition-shadow duration-300 animate-slide-up"
              style={{ animationDelay: `${index * 100}ms` }}
            >
              <div className="relative h-48 bg-secondary-100">
                {project.image && !project.image.includes("placeholder") ? (
                  <img
                    src={project.image}
                    alt={project.title}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Smartphone className="w-16 h-16 text-secondary-400" />
                  </div>
                )}
                <div className="absolute top-4 right-4">
                  <span
                    className={`px-3 py-1 rounded-full text-xs font-medium ${
                      project.category === "ios"
                        ? "bg-blue-100 text-blue-800"
                        : project.category === "android"
                        ? "bg-green-100 text-green-800"
                        : "bg-purple-100 text-purple-800"
                    }`}
                  >
                    {project.category === "cross-platform"
                      ? "Cross-Platform"
                      : project.category.toUpperCase()}
                  </span>
                </div>
              </div>

              <div className="p-6">
                <h3 className="text-xl font-semibold text-secondary-900 mb-3">
                  {project.title}
                </h3>

                <p className="text-secondary-600 mb-4">{project.description}</p>

                <div className="flex flex-wrap gap-2 mb-6">
                  {project.technologies.map((tech, techIndex) => (
                    <span
                      key={techIndex}
                      className="px-3 py-1 bg-primary-100 text-primary-800 text-sm rounded-full"
                    >
                      {tech}
                    </span>
                  ))}
                </div>

                <div className="flex space-x-4">
                  <a
                    href={project.liveUrl}
                    className="flex items-center space-x-2 text-primary-600 hover:text-primary-700 font-medium transition-colors"
                  >
                    <ExternalLink className="w-4 h-4" />
                    <span>Live Demo</span>
                  </a>
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Portfolio Image */}
        <div className="mt-16 mb-16">
          <div className="max-w-4xl mx-auto">
            <div className="relative rounded-2xl overflow-hidden shadow-2xl">
              <img
                src="/images/mobile-app-dev-applanta-3.jpg"
                alt="Our Portfolio and Projects"
                className="w-full h-64 object-cover"
                onError={(e) => {
                  e.target.style.display = "none";
                  e.target.nextSibling.style.display = "flex";
                }}
              />
              <div className="hidden w-full h-64 bg-gradient-to-br from-primary-100 to-accent-100 items-center justify-center">
                <div className="text-center">
                  <div className="w-16 h-16 bg-primary-600 rounded-xl flex items-center justify-center mx-auto mb-4">
                    <Smartphone className="w-8 h-8 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-secondary-800 mb-2">
                    Our Portfolio
                  </h3>
                  <p className="text-secondary-600">
                    Successful mobile app projects
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Call to Action */}
        <div className="text-center mt-16">
          <h3 className="text-2xl font-bold text-secondary-900 mb-4">
            Ready to Start Your Project?
          </h3>
          <p className="text-secondary-600 mb-8">
            Let's discuss how we can bring your mobile app idea to life
          </p>
          <a href="#contact" className="btn-primary">
            Get Started Today
          </a>
        </div>
      </div>
    </section>
  );
}
